import type { ReactNode } from 'react';

/**
 * Convierte las direcciones que haya en un texto en ligas que se puedan tocar.
 *
 * ## Por qué hace falta
 *
 * Un aviso que dice «regístrate en …» con la dirección en texto plano obliga a
 * seleccionarla y copiarla a mano — en un teléfono, con el dedo, sobre un
 * párrafo. La mayoría no lo hace, y el aviso deja de servir para lo que se
 * escribió.
 *
 * ## Cómo escribe la gente una dirección
 *
 * **Casi nunca con `https://` delante.** La primera versión de esto solo
 * reconocía direcciones con esquema, y el primer aviso de verdad que alguien
 * escribió decía `www.google.com`: no se enlazó, y desde fuera parecía que la
 * función no servía.
 *
 * Así que se reconocen las tres formas en que se escribe: con esquema, con
 * `www.` delante, y a secas —`kodica.texelos.com`—. Esta última es la que puede
 * confundirse con texto normal, y por eso se acota a una lista de terminaciones
 * conocidas: sin ella, «a las 9 a.m.» o «Sr.González» acabarían subrayados.
 *
 * ## Por qué se arma con nodos y no con HTML
 *
 * Porque el texto lo escribe una persona y **nunca** se interpreta como
 * marcado. Con `dangerouslySetInnerHTML` bastaría que alguien de la
 * administración pegara un `<script>` —o lo pegara sin querer, copiando de un
 * correo— para ejecutarlo en el teléfono de todos los residentes. Aquí el texto
 * sigue siendo texto: lo único que se construye son elementos `<a>`.
 *
 * ## Qué se enlaza y qué no
 *
 * Solo `http` y `https`. `javascript:` ejecuta código, `data:` puede traer una
 * página entera, y `file:` apunta al aparato de quien lee. Ninguno de los tres
 * tiene por qué aparecer en un aviso del condominio, y el día que aparezca es
 * porque alguien está intentando algo.
 */

/**
 * Las terminaciones que se aceptan en una dirección escrita sin `www.` ni
 * esquema.
 *
 * Corta a propósito. Es la diferencia entre reconocer `kodica.texelos.com` y
 * subrayar media frase: cada terminación que se añade es una forma más de que
 * una abreviatura se vuelva liga por accidente.
 */
const TERMINACIONES = ['com', 'mx', 'org', 'net', 'gob', 'edu', 'app', 'io', 'info'];

/**
 * Direcciones dentro del texto, en las tres formas en que se escriben.
 *
 * La puntuación final se deja fuera: casi nadie escribe una liga y la deja
 * suelta —va seguida de un punto o entre paréntesis— y meterla dentro rompe la
 * dirección.
 */
const DIRECCION = new RegExp(
  [
    // Con esquema: https://loquesea
    String.raw`https?:\/\/[^\s<>]+`,
    // Con www, y sin nada pegado por delante: `www.loquesea`.
    //
    // El `(?<!…)` es lo que impide morder dentro de otra cosa. Sin él,
    // `ftp://www.algo.mx` se partía: el trozo de en medio se reconocía como
    // dirección y se enlazaba **cambiándole el esquema a https**, que es peor
    // que no enlazarlo — se manda a la gente a un sitio distinto del escrito.
    // Y `alguien@dominio.com` acababa con medio correo subrayado.
    String.raw`(?<![\w/@.:-])www\.[^\s<>]+`,
    // A secas, y solo con una terminación conocida: `kodica.texelos.com/algo`.
    String.raw`(?<![\w/@.:-])[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.(?:${TERMINACIONES.join('|')})(?:\.[a-z]{2})?(?:\/[^\s<>]*)?`,
  ]
    // Se recorta la puntuación de cierre, que casi siempre es de la frase.
    .map((forma) => `(?:${forma})`)
    .join('|'),
  'gi',
);

/** Lo que nunca puede quedar al final de una dirección. */
const CIERRE = /[.,;:!?)\]}»"']+$/;

export function conLigas(texto: string): ReactNode[] {
  const salida: ReactNode[] = [];
  let desde = 0;

  for (const encontrada of texto.matchAll(DIRECCION)) {
    const bruta = encontrada[0];
    const inicio = encontrada.index;

    // La puntuación de cierre vuelve al texto, no a la liga.
    const limpia = bruta.replace(CIERRE, '');
    const destino = limpia.startsWith('http') ? limpia : `https://${limpia}`;

    if (!esSegura(destino)) continue;

    if (inicio > desde) salida.push(texto.slice(desde, inicio));
    salida.push(
      <a
        key={inicio}
        href={destino}
        target="_blank"
        // `noopener` para que la página abierta no pueda tocar la nuestra, y
        // `noreferrer` para no contarle de dónde viene quien la abre.
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2"
      >
        {limpia}
      </a>,
    );
    desde = inicio + limpia.length;
  }

  if (desde < texto.length) salida.push(texto.slice(desde));
  return salida;
}

/** Solo `http` y `https`, y solo si el navegador la entiende como dirección. */
function esSegura(posible: string): boolean {
  try {
    const url = new URL(posible);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}
